import { ReportingPermissions } from '@principle-theorem/principle-core/features';
import {
  BrandCollection,
  CustomChartType,
  CustomReportStatus,
  IBaseChartBuilderConfig,
  IBrand,
  ICustomChartSettings,
  ICustomReport,
  ICustomReportChart,
  ICustomReportChartSection,
  ICustomReportColumn,
  ICustomReportDefinedColumn,
  IStaffer,
  ReportingReference,
} from '@principle-theorem/principle-core/interfaces';
import {
  ICanBeChartedProperty,
  IChartBuilderData,
} from '@principle-theorem/reporting/interfaces';
import {
  AtLeast,
  CollectionReference,
  DocumentReference,
  Firestore,
  IReffable,
  WithRef,
  all$,
  asyncForEach,
  multiSortBy$,
  snapshot,
  subCollection,
  undeletedQuery,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { RestrictAccess } from '../auth/restrict-access';
import { Staffer } from '../staffer/staffer';

export class CustomReport {
  static init(
    overrides: AtLeast<ICustomReport, 'name' | 'dataSource' | 'queryScopes'>
  ): ICustomReport {
    return {
      deleted: false,
      restrictAccessTo: [],
      columns: [],
      staticFilters: [],
      chartSections: [],
      status: CustomReportStatus.Draft,
      ...overrides,
    } as ICustomReport;
  }

  static col(brand: IReffable<IBrand>): CollectionReference<ICustomReport> {
    return subCollection<ICustomReport>(
      brand.ref,
      BrandCollection.CustomReports
    );
  }

  static all$(brand: IReffable<IBrand>): Observable<WithRef<ICustomReport>[]> {
    return all$(undeletedQuery(CustomReport.col(brand)));
  }

  static duplicate(report: WithRef<ICustomReport>): ICustomReport {
    return CustomReport.init({
      ...report,
      name: `${report.name} copy`,
      status: CustomReportStatus.Draft,
    });
  }

  static stafferAllowedCustomReports$(
    brand: IReffable<IBrand>,
    staffer: WithRef<IStaffer>
  ): Observable<WithRef<ICustomReport>[]> {
    return CustomReport.all$(brand).pipe(
      switchMap(async (reports) =>
        asyncForEach(reports, async (report) =>
          (await CustomReport.userHasAccess(report, staffer))
            ? report
            : undefined
        )
      ),
      map(compact),
      multiSortBy$((report) => report.name)
    );
  }

  static async userHasAccess(
    report: WithRef<ICustomReport>,
    staffer?: WithRef<IStaffer>
  ): Promise<boolean> {
    if (!staffer) {
      return false;
    }
    const canEdit = await snapshot(
      Staffer.hasPermission$(
        staffer,
        ReportingPermissions.ReportingCustomReportEdit
      )
    );
    if (canEdit) {
      return true;
    }
    const accessEntities = await snapshot(
      RestrictAccess.getAccessEntities$(staffer)
    );
    return RestrictAccess.hasAccess(report.restrictAccessTo, accessEntities);
  }

  static chartSection(
    name: string,
    charts: ICustomReportChart[]
  ): ICustomReportChartSection {
    return { uid: uuid(), name, charts };
  }

  static chart<
    T extends ICustomChartSettings,
    Config extends IBaseChartBuilderConfig = IBaseChartBuilderConfig,
  >(
    type: CustomChartType,
    settings: T[],
    label: string,
    groupBy: ReportingReference,
    builderData?: Partial<
      Omit<IChartBuilderData, 'measures' | 'groupByDimension'>
    >,
    builderConfig?: Partial<Config>
  ): ICustomReportChart<T> {
    return {
      uid: uuid(),
      type,
      settings,
      label,
      groupBy,
      builderData,
      builderConfig,
    };
  }

  static column(id: string, label?: string): ICustomReportColumn {
    return { uid: uuid(), id, label };
  }

  static definedColumn(
    measure: ICanBeChartedProperty,
    id: string,
    label?: string
  ): ICustomReportDefinedColumn {
    return { uid: uuid(), id, label, measure };
  }

  static brandRef(report: IReffable<ICustomReport>): DocumentReference<IBrand> {
    return Firestore.getParentDocRef<IBrand>(report.ref);
  }
}
